import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../api';
import { isRecord } from '../payloadGuards';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface SupportMessage {
  _id: string;
  ticketId: string;
  message: string;
  sender: 'user' | 'admin';
  senderName: string;
  createdAt: string;
}

export interface SupportTicket {
  _id: string;
  subject: string;
  category: string;
  status: 'open' | 'pending' | 'resolved' | 'closed';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  userId: string;
  userEmail: string;
  userName: string;
  practiceId?: string;
  practiceName?: string;
  messages: SupportMessage[];
  createdAt: string;
  updatedAt: string;
}

interface SupportState {
  tickets: SupportTicket[];
  selectedTicket: SupportTicket | null;
  isLoading: boolean;
  isSending: boolean;
  error: string | null;
  total: number;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function parseBody<T>(raw: unknown): T {
  if (raw && typeof raw === 'object' && 'body' in (raw as object)) {
    const body = (raw as Record<string, unknown>).body;
    if (typeof body === 'string') {
      try { return JSON.parse(body) as T; } catch { return body as unknown as T; }
    }
    return body as T;
  }
  return raw as T;
}

// ─── Thunks ───────────────────────────────────────────────────────────────────

export const createSupportTicket = createAsyncThunk(
  'support/createTicket',
  async (
    payload: { subject: string; message: string; category: string; priority?: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post('/support/ticket', payload);
      return parseBody<SupportTicket>(response.data);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to create ticket');
    }
  }
);

export const fetchMyTickets = createAsyncThunk(
  'support/fetchMyTickets',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/support/tickets/me');
      const data = parseBody<{ tickets?: SupportTicket[]; total?: number } | SupportTicket[]>(response.data);
      if (Array.isArray(data)) return { tickets: data, total: data.length };
      return { tickets: (data as { tickets?: SupportTicket[] }).tickets ?? [], total: (data as { total?: number }).total ?? 0 };
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch tickets');
    }
  }
);

export const fetchTicketById = createAsyncThunk(
  'support/fetchTicketById',
  async (ticketId: string, { rejectWithValue }) => {
    try {
      const response = await api.get(`/support/tickets/${ticketId}`);
      return parseBody<SupportTicket>(response.data);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch ticket');
    }
  }
);

export const replyToTicket = createAsyncThunk(
  'support/replyToTicket',
  async ({ ticketId, message }: { ticketId: string; message: string }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/support/tickets/${ticketId}/reply`, { message });
      return parseBody<SupportMessage>(response.data);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to send reply');
    }
  }
);

// ─── Slice ────────────────────────────────────────────────────────────────────

const initialState: SupportState = {
  tickets: [],
  selectedTicket: null,
  isLoading: false,
  isSending: false,
  error: null,
  total: 0,
};

const supportSlice = createSlice({
  name: 'support',
  initialState,
  reducers: {
    clearSelectedTicket: (state) => { state.selectedTicket = null; },
    clearError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      // createSupportTicket
      .addCase(createSupportTicket.pending, (state) => { state.isSending = true; state.error = null; })
      .addCase(createSupportTicket.fulfilled, (state, action) => {
        state.isSending = false;
        if (isRecord(action.payload)) state.tickets.unshift(action.payload);
      })
      .addCase(createSupportTicket.rejected, (state, action) => {
        state.isSending = false;
        state.error = action.payload as string;
      })
      // fetchMyTickets
      .addCase(fetchMyTickets.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(fetchMyTickets.fulfilled, (state, action) => {
        state.isLoading = false;
        state.tickets = action.payload.tickets;
        state.total = action.payload.total;
      })
      .addCase(fetchMyTickets.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // fetchTicketById
      .addCase(fetchTicketById.pending, (state) => { state.isLoading = true; })
      .addCase(fetchTicketById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedTicket = action.payload;
      })
      .addCase(fetchTicketById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // replyToTicket
      .addCase(replyToTicket.pending, (state) => { state.isSending = true; })
      .addCase(replyToTicket.fulfilled, (state, action) => {
        state.isSending = false;
        if (state.selectedTicket && action.payload) {
          state.selectedTicket.messages = [
            ...(state.selectedTicket.messages ?? []),
            action.payload,
          ];
        }
      })
      .addCase(replyToTicket.rejected, (state, action) => {
        state.isSending = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearSelectedTicket, clearError } = supportSlice.actions;
export default supportSlice.reducer;
